import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Download, ExternalLink, X } from 'lucide-react';
import { SharePointFile } from '@/hooks/useSharePointFiles';

interface FilePreviewDialogProps {
  file: SharePointFile | null;
  files: SharePointFile[];
  onClose: () => void;
  onNavigate: (direction: 'prev' | 'next') => void;
}

export function FilePreviewDialog({ file, files, onClose, onNavigate }: FilePreviewDialogProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (file) {
      const index = files.findIndex(f => f.id === file.id);
      setCurrentIndex(index);
    }
  }, [file, files]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!file) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        handleNavigate('prev');
      } else if (e.key === 'ArrowRight') {
        handleNavigate('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [file, onClose]);

  const handleNavigate = (direction: 'prev' | 'next') => {
    const nonFolderFiles = files.filter(f => !f.folder);
    const currentFileIndex = nonFolderFiles.findIndex(f => f.id === file?.id);
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentFileIndex > 0 ? currentFileIndex - 1 : nonFolderFiles.length - 1;
    } else {
      newIndex = currentFileIndex < nonFolderFiles.length - 1 ? currentFileIndex + 1 : 0;
    }
    
    onNavigate(direction);
  };

  if (!file) return null;

  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
    file.name.split('.').pop()?.toLowerCase() || ''
  );
  const isPdf = file.name.toLowerCase().endsWith('.pdf');
  const nonFolderFiles = files.filter(f => !f.folder);
  const canNavigate = nonFolderFiles.length > 1;

  return (
    <Dialog open={!!file} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="truncate pr-8">{file.name}</DialogTitle>
            <div className="flex gap-2">
              {file.downloadUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(file.downloadUrl, '_blank')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              )}
              {file.webUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(file.webUrl, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in SharePoint
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden relative bg-muted/20 rounded-lg">
          {isImage && file.downloadUrl ? (
            <div className="h-full flex items-center justify-center p-4">
              <img
                src={file.downloadUrl}
                alt={file.name}
                className="max-w-full max-h-full object-contain"
                style={{ imageOrientation: 'from-image' }}
              />
            </div>
          ) : isPdf && file.downloadUrl ? (
            <iframe
              src={file.downloadUrl}
              className="w-full h-full min-h-[600px]"
              title={file.name}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-8 text-center">
              <p className="text-muted-foreground mb-4">
                Preview niet beschikbaar voor dit bestandstype
              </p>
              <div className="space-y-2">
                <p className="text-sm">
                  <strong>Bestandsnaam:</strong> {file.name}
                </p>
                <p className="text-sm">
                  <strong>Type:</strong> {file.mimeType || 'Onbekend'}
                </p>
                <p className="text-sm">
                  <strong>Grootte:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              {file.webUrl && (
                <Button
                  className="mt-6"
                  onClick={() => window.open(file.webUrl, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in SharePoint voor preview
                </Button>
              )}
            </div>
          )}

          {canNavigate && (
            <>
              <Button
                variant="secondary"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2"
                onClick={() => handleNavigate('prev')}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2"
                onClick={() => handleNavigate('next')}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
        </div>

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div>
            {canNavigate && (
              <span>
                {nonFolderFiles.findIndex(f => f.id === file.id) + 1} van {nonFolderFiles.length}
              </span>
            )}
          </div>
          <div>
            Gebruik pijltjestoetsen om te navigeren • ESC om te sluiten
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
